import { useMsal } from '@azure/msal-react';
import useSWR from 'swr';
import { getOverview, type AnalyticsRange } from '@/services/api';
import type { OverviewResponse } from '@/types/overview';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PortfolioKpiStrip } from './PortfolioKpiStrip';
import { PortfolioTrend } from './PortfolioTrend';
import { AccountsTable } from './AccountsTable';
import { RecentlyActiveAccounts } from './RecentlyActiveAccounts';

export type SentimentScope = 'all' | 'up' | 'down';

/**
 * Products Overview: the default (no-account-selected) landing state on the
 * Feedback page. Rolls feedback up across all accounts for the current window;
 * selecting an account (row/list click, or the dropdown above) drills into the
 * existing per-account dashboard.
 *
 * The sentiment scope is controlled by the parent (it lives in the shared
 * Products-overview filter strip alongside account/scope/range).
 */
export function PortfolioOverview({
  range,
  mine,
  sentiment,
  onSelectAccount,
}: {
  range: AnalyticsRange;
  mine: boolean;
  sentiment: SentimentScope;
  onSelectAccount: (accountToken: string) => void;
}) {
  const { instance } = useMsal();
  const rangeKey = 'days' in range ? `d${range.days}` : `${range.from}_${range.to}`;

  const { data, error, isLoading } = useSWR<OverviewResponse>(
    `overview-${rangeKey}-${mine}-${sentiment}`,
    () => getOverview(instance, range, mine, sentiment === 'all' ? undefined : sentiment)
  );

  let body: React.ReactNode;
  if (isLoading) {
    body = (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-[260px]" />
        <div className="grid gap-4 md:grid-cols-[1.6fr_1fr]">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  } else if (error) {
    body = (
      <Alert variant="destructive">
        <AlertDescription>Failed to load the products overview. Select an account above to view its dashboard.</AlertDescription>
      </Alert>
    );
  } else if (!data || data.totalAccounts === 0) {
    body = (
      <div className="rounded-md border bg-muted/50 py-8 text-center text-muted-foreground">
        No accounts to summarise{mine ? ' for you' : ''}{sentiment !== 'all' ? ' for that sentiment' : ''} yet.
      </div>
    );
  } else {
    body = (
      <>
        <PortfolioKpiStrip overview={data} />
        <PortfolioTrend daily={data.dailyBreakdown} />
        <div className="grid gap-4 md:grid-cols-[1.6fr_1fr]">
          <AccountsTable accounts={data.accounts} onSelectAccount={onSelectAccount} />
          <RecentlyActiveAccounts accounts={data.accounts} onSelectAccount={onSelectAccount} />
        </div>
      </>
    );
  }

  return (
    <div className="space-y-4" data-testid="products-overview">
      {body}
    </div>
  );
}
